import React, { useState, useEffect } from 'react';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import ChatInterface from './ChatInterface';
import ContextPanel from './ContextPanel';
import api from '../api/client';

export default function Dashboard({ onLogout }) {
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Drives what shows up in the right-hand context panel.
  // intent: the classified intent from the latest assistant response
  //         (e.g., 'risk_assessment', 'compliance_status', etc.)
  // metadata: the full response payload, in case the panel wants to
  //           render specific data from the backend (workflow_id, entities, etc.)
  const [contextState, setContextState] = useState({
    intent: null,
    metadata: null,
  });

  useEffect(() => {
    loadCustomerInfo();
  }, []);

  const loadCustomerInfo = async () => {
    try {
      const info = await api.getCustomerInfo();
      setCustomerInfo(info);
    } catch (error) {
      console.error('Failed to load customer info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Called by ChatInterface whenever an assistant response comes back.
  // We update the right pane to reflect the new intent.
  const handleIntentDetected = (intent, metadata) => {
    setContextState({ intent, metadata });
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-4">
              <img
                src="/moyer-logo.png"
                alt="Moyer Scientific Research"
                className="h-10 w-auto"
              />
              <div className="border-l border-gray-600 pl-4">
                <h1 className="text-lg font-semibold text-white tracking-wide">CISO Advisor</h1>
                {customerInfo && (
                  <p className="text-xs text-gray-400 font-medium">
                    {customerInfo.company_name || 'Loading...'}
                  </p>
                )}
              </div>
            </div>

            {/* Logout */}
            <button
              onClick={onLogout}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors font-medium"
            >
              <span className="text-sm">Sign Out</span>
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content - two-pane layout */}
      <main className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <div className="h-full flex flex-col lg:flex-row">
            {/* Left pane: Chat (40% on lg+, full width on smaller screens) */}
            <div className="w-full lg:w-2/5 lg:border-r border-gray-700 flex flex-col overflow-hidden">
              <ChatInterface onIntentDetected={handleIntentDetected} />
            </div>

            {/* Right pane: Contextual visualization (60% on lg+) */}
            <div className="w-full lg:w-3/5 flex flex-col overflow-hidden bg-gray-900">
              <ContextPanel
                intent={contextState.intent}
                metadata={contextState.metadata}
                customerInfo={customerInfo}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
